import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { BackIcon, MarkdownViewer } from "@expert/solve-tools";
import { ActionIcon, Box, Flex, Group, Menu, Space, Stack, Text, UnstyledButton } from "@mantine/core";
import { useCallback, useMemo } from "react";

import type { ExpertAssistMessage } from "../../shared-types";

import { QuickActionGroup, toAIQuickActions } from "../quick-actions";
import { formatRelativeTime, getGuidancePreviewLabel } from "./guidancePreview";
import { proactiveGuidanceMarkdownOptions } from "./markdownOverrides";
import styles from "./styles.module.css";
import { useProactiveGuidanceHistory } from "./useProactiveGuidanceHistory";

interface Props {
    messages: ExpertAssistMessage[];
    currentSessionId: string;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export function ProactiveGuidanceMessageBubble({
    messages,
    currentSessionId,
    logger,
    gaiaPayloadProps,
    scrollRef,
}: Props) {
    const { guidanceMessages, currentMessage, shouldShowNav, page, total, setPage } = useProactiveGuidanceHistory(
        messages,
        currentSessionId,
    );
    const { dispatcher } = useReactAnalytics();
    const canGoBack = page > 1;
    const canGoForward = page < total;
    const scrollActiveIntoView = useCallback((node: HTMLButtonElement | null) => {
        const container = node?.closest<HTMLElement>("[data-suggestion-scroll]");
        if (!node || !container) return;
        const offset = node.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += offset - (container.clientHeight - node.clientHeight) / 2;
    }, []);

    const goToPage = useCallback(
        (method: "previous" | "next" | "jump", toPage: number) => {
            void dispatcher.dispatchBusinessEvent("ProactiveGuidance_SuggestionNavigated", {
                method,
                fromPage: page,
                toPage,
                total,
                messageId: guidanceMessages[toPage - 1]?.id,
            });
            setPage(toPage);
        },
        [dispatcher, page, total, guidanceMessages, setPage],
    );

    const actions = useMemo(() => toAIQuickActions(currentMessage?.metadata?.aiQuickActions), [currentMessage]);
    if (!currentMessage) return null;

    return (
        <Flex className={styles.slot} justify="center">
            <Stack className={styles.card} data-testid="proactive-guidance-message-bubble" gap={0}>
                <Stack className={styles.cardScroll} gap={0}>
                    <Group justify="space-between" align="center" wrap="nowrap">
                        <Text size="sm" c="dark.2">
                            {currentMessage.tag ?? "Suggested guidance"}
                        </Text>
                        {shouldShowNav && (
                            <Group gap="4xs" wrap="nowrap">
                                <ActionIcon
                                    aria-label="Previous guidance"
                                    className={styles.navButton}
                                    disabled={!canGoBack}
                                    onClick={() => goToPage("previous", page - 1)}
                                    size="md"
                                    variant="transparent"
                                >
                                    <BackIcon height={18} width={18} />
                                </ActionIcon>
                                <Menu position="bottom" width={280} withinPortal>
                                    <Menu.Target>
                                        <UnstyledButton
                                            aria-label="Jump to a suggestion"
                                            className={styles.counterButton}
                                        >
                                            <Text c="dark.2" size="xs">
                                                {page} of {total}
                                            </Text>
                                        </UnstyledButton>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Label>Jump to a suggestion</Menu.Label>
                                        <Box className={styles.suggestionScroll} data-suggestion-scroll>
                                            {guidanceMessages.map((message, index) => (
                                                <Menu.Item
                                                    className={styles.previewItem}
                                                    data-active={index + 1 === page || undefined}
                                                    key={message.id}
                                                    onClick={() => goToPage("jump", index + 1)}
                                                    ref={index + 1 === page ? scrollActiveIntoView : undefined}
                                                >
                                                    <Stack gap={2}>
                                                        <Text lineClamp={1} size="sm">
                                                            {getGuidancePreviewLabel(message.text)}
                                                        </Text>
                                                        <Text c="dimmed" size="xs">
                                                            {formatRelativeTime(message.timestamp)}
                                                        </Text>
                                                    </Stack>
                                                </Menu.Item>
                                            ))}
                                        </Box>
                                    </Menu.Dropdown>
                                </Menu>
                                <ActionIcon
                                    aria-label="Next guidance"
                                    className={styles.navButton}
                                    disabled={!canGoForward}
                                    onClick={() => goToPage("next", page + 1)}
                                    size="md"
                                    variant="transparent"
                                >
                                    <BackIcon className={styles.forwardIcon} height={18} width={18} />
                                </ActionIcon>
                            </Group>
                        )}
                    </Group>
                    <MarkdownViewer content={currentMessage.text} options={proactiveGuidanceMarkdownOptions} />
                    <Space h="sm" />
                    {actions.length > 0 && (
                        <QuickActionGroup
                            actions={actions}
                            gaiaPayloadProps={gaiaPayloadProps}
                            logger={logger}
                            scrollRef={scrollRef}
                            setIsQuickActionsVisible={() => undefined}
                        />
                    )}
                </Stack>
            </Stack>
        </Flex>
    );
}
